import { i18n } from '../i18n.js';
import { Firebase } from '../firebase-loader.js';

/**
 * "WITHDRAW FROM CONTRACT HERE" (Consumer Rights Directive Art. 11a, from
 * Directive (EU) 2023/2673, applicable since 19 June 2026; launch audit round 3,
 * 2026-09-26).
 *
 * A player withdraws from a purchase inside the Service: the purchases still
 * withdrawable are listed with what the law refunds, each behind a button
 * labelled "Withdraw from contract here"; a second step shows who is
 * withdrawing from what, where the confirmation goes, and a "Confirm
 * withdrawal" button; the server records the statement with its date and time,
 * runs the refund, and the acknowledgement shows both — with a copy to save.
 * Stripe's refund receipt email carries the refund to the player's inbox.
 *
 * Reached from the store's footer and the account menu, so it is available
 * wherever a purchase is made, for as long as one can be withdrawn from.
 * Wears the Privacy & Data panel's frame (gdpr-settings.css).
 */

const WithdrawalPanel = (function () {
  let overlay = null;
  // Where focus returns when the panel closes, and whether a submit is in
  // flight (Escape does not abandon a withdrawal half-sent).
  let opener = null;
  let submitting = false;

  function uiText(key, fallback, replacements = {}) {
    const translated = i18n?.t?.(key, replacements);
    if (translated && translated !== key) return translated;
    return String(fallback).replace(/\{\{(\w+)\}\}/g, (match, token) => (
      Object.prototype.hasOwnProperty.call(replacements, token) ? String(replacements[token]) : match
    ));
  }

  function el(tag, attrs = {}, children = []) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (key === 'className') node.className = value;
      else if (key === 'textContent') node.textContent = value;
      else node.setAttribute(key, value);
    }
    for (const child of children) {
      node.appendChild(typeof child === 'string' ? document.createTextNode(child) : child);
    }
    return node;
  }

  function locale() {
    return i18n?.getLanguage?.() || document.documentElement.lang || undefined;
  }

  function formatMoney(amountMinor, currency) {
    const amount = Number(amountMinor) / 100;
    try {
      return new Intl.NumberFormat(locale(), { style: 'currency', currency: String(currency || 'usd').toUpperCase() })
        .format(amount);
    } catch (_) {
      return `${amount.toFixed(2)} ${String(currency || '').toUpperCase()}`;
    }
  }

  function formatDay(at) {
    try {
      return new Date(Number(at)).toLocaleDateString(locale(), { year: 'numeric', month: 'long', day: 'numeric' });
    } catch (_) {
      return '';
    }
  }

  function formatMoment(at) {
    try {
      return new Date(Number(at)).toLocaleString(locale(), {
        year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZoneName: 'short',
      });
    } catch (_) {
      return new Date(Number(at)).toISOString();
    }
  }

  function productName(purchase) {
    if (purchase?.purchaseType === 'moonlight_refill') {
      return uiText('withdrawal.productRefill', 'Moonlight refill');
    }
    if (purchase?.purchaseType === 'stardust_purchase') {
      return uiText('withdrawal.productStardust', '{{n}} Stardust', {
        n: Number(purchase.stardustAmount || 0).toLocaleString(locale()),
      });
    }
    return purchase?.subscriptionTier === 'moonfall_storyteller'
      ? uiText('store.storytellerPass', 'Storyteller Pass')
      : uiText('store.mangaPass', 'Manga Pass');
  }

  async function authHeaders() {
    const token = await Firebase.getIdToken();
    if (!token) throw new Error('signed_out');
    return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
  }

  function close() {
    if (!overlay) return;
    const node = overlay;
    overlay = null;
    submitting = false;
    node.classList.remove('visible');
    setTimeout(() => node.remove(), 300);
    const returnTo = opener;
    opener = null;
    if (returnTo && returnTo.isConnected) returnTo.focus?.();
  }

  function title(text) {
    return el('h3', { id: 'withdrawal-title', textContent: text });
  }

  function frame(children) {
    const modal = el('div', {
      className: 'gdpr-delete-modal gdpr-privacy-modal withdrawal-modal',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': 'withdrawal-title',
    }, children);
    return modal;
  }

  function swap(modalChildren, focusTarget) {
    if (!overlay) return;
    overlay.replaceChildren(frame(modalChildren));
    focusTarget?.focus?.();
  }

  function closeButton(label = uiText('store.close', 'Close')) {
    const button = el('button', { type: 'button', className: 'gdpr-delete-cancel', textContent: label });
    button.addEventListener('click', close);
    return button;
  }

  function supportLine() {
    return el('p', {
      className: 'withdrawal-support',
      textContent: uiText('withdrawal.support', 'You can also withdraw by writing to support@moonfall.io.'),
    });
  }

  function acknowledgementText(purchase, acknowledgment) {
    const amount = formatMoney(acknowledgment.refundAmountMinor, acknowledgment.currency);
    const at = formatMoment(acknowledgment.submittedAt);
    return acknowledgment.status === 'refunded'
      ? uiText(
        'withdrawal.receivedRefunded',
        'We received your withdrawal from {{product}} on {{submittedAt}}. {{amount}} is refunded to the payment method you used; banks usually show it within 5–10 business days.',
        { product: productName(purchase), submittedAt: at, amount },
      )
      : uiText(
        'withdrawal.receivedProcessing',
        'We received your withdrawal from {{product}} on {{submittedAt}}. Your refund of {{amount}} is on its way to the payment method you used, within 14 days.',
        { product: productName(purchase), submittedAt: at, amount },
      );
  }

  function saveCopy(purchase, acknowledgment, identity) {
    const lines = [
      uiText('withdrawal.receivedTitle', 'Withdrawal received'),
      '',
      acknowledgementText(purchase, acknowledgment),
      '',
      `${uiText('withdrawal.copyReference', 'Reference')}: ${acknowledgment.withdrawalId}`,
      `${uiText('withdrawal.copySubmitted', 'Submitted')}: ${new Date(Number(acknowledgment.submittedAt)).toISOString()}`,
      identity?.name ? `${uiText('withdrawal.copyName', 'Name')}: ${identity.name}` : '',
      identity?.email ? `${uiText('withdrawal.copyEmail', 'Email')}: ${identity.email}` : '',
      '',
      'Moonfall — support@moonfall.io',
    ].filter((line, index, all) => line !== '' || (all[index - 1] !== ''));
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = el('a', { href: url, download: `moonfall-withdrawal-${acknowledgment.withdrawalId}.txt` });
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  function showAcknowledgement(purchase, acknowledgment, identity) {
    const save = el('button', {
      type: 'button',
      className: 'gdpr-action-btn',
      id: 'withdrawal-save-copy',
      textContent: uiText('withdrawal.saveCopy', 'Save a copy'),
    });
    save.addEventListener('click', () => saveCopy(purchase, acknowledgment, identity));
    swap([
      title(uiText('withdrawal.receivedTitle', 'Withdrawal received')),
      el('p', { className: 'withdrawal-ack', role: 'status', textContent: acknowledgementText(purchase, acknowledgment) }),
      el('div', { className: 'gdpr-delete-actions' }, [save, closeButton()]),
    ], save);
  }

  /* THE 60-DAY LIMIT, SAID BEFORE THE PRESS (owner, 2026-09-28: "i think we
     should warn players about it when pressing withdraw button"). Passes and
     refills only — the rule does not cover Stardust. The date is the one the
     server's own rule computed for a withdrawal made now, sent with the list
     (`withdrawalPauseIfWithdrawnUntil`), never a guess made here; a list
     without it gets the rule itself. It informs and never delays or blocks
     the withdrawal. */
  function limitLine(purchase, pauseUntil) {
    if (purchase?.purchaseType !== 'subscription_charge' && purchase?.purchaseType !== 'moonlight_refill') return null;
    const until = Number(pauseUntil);
    const text = Number.isFinite(until) && until > 0
      ? uiText(
        'withdrawal.limitPause',
        'You withdrew from a pass or refill in the last 60 days, so after this withdrawal new passes and refills are unavailable until {{date}}.',
        { date: formatDay(until) },
      )
      : uiText(
        'withdrawal.limitRule',
        'Withdrawing from passes or refills twice within 60 days pauses new passes and refills until 60 days after the first of the two.',
      );
    return el('p', { className: 'withdrawal-limit', textContent: text });
  }

  function showConfirmation(purchase, identity, pauseUntil) {
    const status = el('p', { className: 'withdrawal-status', role: 'alert' });
    const confirm = el('button', {
      type: 'button',
      className: 'gdpr-delete-confirm withdrawal-confirm',
      id: 'withdrawal-confirm',
      textContent: uiText('withdrawal.confirmButton', 'Confirm withdrawal'),
    });
    const back = el('button', { type: 'button', className: 'gdpr-delete-cancel', textContent: uiText('common.cancel', 'Cancel') });
    back.addEventListener('click', () => { void open(); });
    confirm.addEventListener('click', async () => {
      confirm.disabled = true;
      back.disabled = true;
      submitting = true;
      status.textContent = '';
      try {
        const response = await fetch('/api/withdrawals', {
          method: 'POST',
          headers: await authHeaders(),
          body: JSON.stringify({ billingPurchaseId: purchase.billingPurchaseId }),
        });
        const payload = await response.json().catch(() => null);
        if (!response.ok || !payload?.acknowledgment) {
          throw new Error(payload?.code || `http_${response.status}`);
        }
        submitting = false;
        showAcknowledgement(purchase, payload.acknowledgment, identity);
      } catch (error) {
        submitting = false;
        console.warn('[Withdrawal] Submit failed:', error?.message || error);
        status.textContent = uiText(
          'withdrawal.submitFailed',
          'We could not submit your withdrawal. Please try again, or write to support@moonfall.io.',
        );
        confirm.disabled = false;
        back.disabled = false;
      }
    });
    swap([
      title(uiText('withdrawal.confirmTitle', 'Confirm your withdrawal')),
      el('p', {
        textContent: uiText(
          'withdrawal.confirmDetail',
          'Withdrawal from {{product}}, bought on {{date}}, by {{name}}. We confirm receipt at {{email}} and refund {{amount}} to the payment method you used.',
          {
            product: productName(purchase),
            date: formatDay(purchase.purchasedAt),
            name: identity?.name || '—',
            email: identity?.email || '—',
            amount: formatMoney(purchase.refundAmountMinor, purchase.currency),
          },
        ),
      }),
      limitLine(purchase, pauseUntil),
      status,
      el('div', { className: 'gdpr-delete-actions' }, [back, confirm]),
    ].filter(Boolean), confirm);
  }

  function purchaseRow(purchase, identity, pauseUntil) {
    const button = el('button', {
      type: 'button',
      className: 'gdpr-action-btn withdrawal-button',
      'data-billing-purchase-id': String(purchase.billingPurchaseId),
      textContent: uiText('withdrawal.withdrawButton', 'Withdraw from contract here'),
    });
    button.addEventListener('click', () => showConfirmation(purchase, identity, pauseUntil));
    return el('div', { className: 'gdpr-action-row withdrawal-row' }, [
      el('div', { className: 'gdpr-action-info' }, [
        el('div', { className: 'gdpr-action-label', textContent: productName(purchase) }),
        el('div', {
          className: 'gdpr-action-desc',
          textContent: uiText('withdrawal.dates', 'Bought {{bought}} · can be withdrawn until {{until}}', {
            bought: formatDay(purchase.purchasedAt),
            until: formatDay(purchase.withdrawableUntil),
          }),
        }),
        el('div', {
          className: 'gdpr-action-desc',
          textContent: uiText('withdrawal.refund', 'Refund if you withdraw now: {{amount}}', {
            amount: formatMoney(purchase.refundAmountMinor, purchase.currency),
          }),
        }),
      ]),
      button,
    ]);
  }

  function mount(children, focusTarget) {
    if (!overlay) {
      opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      overlay = el('div', { className: 'gdpr-delete-overlay gdpr-privacy-overlay withdrawal-overlay' });
      overlay.addEventListener('click', (event) => { if (event.target === overlay && !submitting) close(); });
      overlay.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || submitting) return;
        event.preventDefault();
        event.stopPropagation();
        close();
      });
      document.body.appendChild(overlay);
      overlay.replaceChildren(frame(children));
      // Focus once shown: the overlay is display:none until `.visible`, and a
      // hidden element cannot take focus.
      requestAnimationFrame(() => {
        overlay?.classList.add('visible');
        focusTarget?.focus?.();
      });
      return;
    }
    swap(children, focusTarget);
  }

  /** Opens with the list already loaded, so nothing pops in after it shows. */
  async function open() {
    let data = null;
    let failed = false;
    try {
      const response = await fetch('/api/withdrawals', { headers: await authHeaders() });
      if (!response.ok) throw new Error(`http_${response.status}`);
      data = await response.json();
    } catch (error) {
      console.warn('[Withdrawal] Load failed:', error?.message || error);
      failed = true;
    }
    const identity = { name: data?.name || '', email: data?.email || '' };
    const pauseUntil = data?.withdrawalPauseIfWithdrawnUntil ?? null;
    const purchases = Array.isArray(data?.purchases) ? data.purchases : [];
    const body = failed
      ? [el('p', { className: 'withdrawal-empty', textContent: uiText('withdrawal.loadFailed', 'We could not load your purchases. Please try again.') })]
      : purchases.length === 0
        ? [el('p', { className: 'withdrawal-empty', textContent: uiText('withdrawal.empty', 'You have no purchases you can withdraw from right now.') })]
        : purchases.map((purchase) => purchaseRow(purchase, identity, pauseUntil));
    const done = closeButton();
    mount([
      title(uiText('withdrawal.title', 'Withdraw from a purchase')),
      el('p', {
        textContent: uiText(
          'withdrawal.intro',
          'Within 14 days of buying, you can withdraw from a purchase here. A pass or refill is refunded for the time not yet used; unspent Stardust is refunded at the price you paid.',
        ),
      }),
      el('div', { className: 'withdrawal-list' }, body),
      supportLine(),
      el('div', { className: 'gdpr-delete-actions' }, [done]),
    ], body.find?.((node) => node.querySelector?.('.withdrawal-button'))?.querySelector('.withdrawal-button') || done);
  }

  return { open, close };
})();

export { WithdrawalPanel };
